/* CheckInNowModal — iter98.99dx Phase B → iter102.22 palette
 *
 * Single-click outreach modal triggered from the amber "Check in" button
 * on each compliance row (queue page + PES home rows). Drops the click-
 * to-outreach count from 4 (open case → switch to chat tab → type message
 * → send) down to 1: pick template, optionally tweak, send.
 *
 * iter102.22 palette:
 *   Trigger button = amber (attention-needed workflow), so the modal
 *   header / template picker / textarea focus / submit CTA all follow
 *   the same amber accent for a seamless "one tone per action" story.
 *
 * Templates are intentionally hard-coded on the FE so the coach can read
 * them at a glance — these are short, friendly nudges, not legal copy.
 * Each template body is fully editable in the textarea before send.
 *
 * Sends via POST /coe/compliance/cases/{case_id}/thread/messages — the
 * same endpoint the deep case page uses. Existing thread-required check
 * lives server-side; if the thread isn't bootstrapped yet (pre-fitment),
 * we surface that error inline rather than crashing the modal.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Heart, ChatCircleDots, Clock, Bandaids, Sparkle, PaperPlaneTilt, X,
} from "@phosphor-icons/react";


const TEMPLATES = [
    {
        key: "checkin",
        icon: ChatCircleDots,
        label: "General check-in",
        helper: "Open-ended, friendly nudge.",
        body: (firstName) =>
            `Hi ${firstName}! Just checking in — how are things going with your aligners this week? 💜`,
    },
    {
        key: "wear_nudge",
        icon: Clock,
        label: "Wear-time nudge",
        helper: "Use when wear-time is dipping.",
        body: (firstName) =>
            `Hey ${firstName} — I noticed your wear-time has dipped a bit. Aiming for 22+ hours a day keeps the plan on track. Anything making it hard to keep them in?`,
    },
    {
        key: "pain_check",
        icon: Bandaids,
        label: "Discomfort check",
        helper: "After a fresh tray change.",
        body: (firstName) =>
            `Hi ${firstName}, hope the new tray is settling well. Any sharp pain or pressure points? A bit of tightness for 24-48h is normal, but flag anything sharper.`,
    },
    {
        key: "celebrate",
        icon: Sparkle,
        label: "Celebrate progress",
        helper: "When wear-time is on point.",
        body: (firstName) =>
            `Hi ${firstName}! Your wear-time this week is looking great — keep it up. You're nailing this.`,
    },
];


function deriveFirstName(fullName) {
    const raw = (fullName || "").trim();
    const noPrefix = raw.replace(/^(dr\.?|doctor)\s+/i, "");
    return noPrefix.split(/\s+/)[0] || "there";
}


export default function CheckInNowModal({ open, onClose, caseRow, onSent }) {
    const [activeKey, setActiveKey] = useState(TEMPLATES[0].key);
    const [body, setBody] = useState(TEMPLATES[0].body(deriveFirstName(caseRow?.patient_name)));
    const [sending, setSending] = useState(false);

    // Re-seed body when the case (or template) changes.
    React.useEffect(() => {
        if (!open) return;
        const tpl = TEMPLATES.find((t) => t.key === activeKey) || TEMPLATES[0];
        setBody(tpl.body(deriveFirstName(caseRow?.patient_name)));
    }, [activeKey, caseRow?.id, open]);

    if (!open || !caseRow) return null;

    const trimmed = body.trim();
    const canSend = trimmed.length > 0 && !sending;

    const handleSend = async () => {
        if (!canSend) return;
        setSending(true);
        try {
            await api.post(
                `/coe/compliance/cases/${caseRow.id}/thread/messages`,
                { body: trimmed },
            );
            toast.success(`Check-in sent to ${caseRow.patient_name?.split(" ")[0] || "patient"}`);
            onSent?.();
            onClose();
        } catch (err) {
            const detail = err.response?.data?.detail || "Couldn't send the check-in";
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
            onClick={onClose}
            data-testid="checkin-now-modal"
        >
            <div
                className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] min-h-[500px] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                                <Heart size={16} weight="duotone" />
                            </div>
                            <div className="min-w-0">
                                <div className="font-display text-base font-bold leading-tight truncate">
                                    Check in with {caseRow.patient_name || "patient"}
                                </div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                    {caseRow.code} · {caseRow.doctor_name}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full grid place-items-center hover:bg-secondary text-muted-foreground transition-colors"
                        data-testid="checkin-now-close"
                    >
                        <X size={14} weight="bold" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
                    <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                        Pick a template
                    </div>
                    <div className="grid grid-cols-2 gap-2" data-testid="checkin-templates">
                        {TEMPLATES.map((t) => {
                            const TIcon = t.icon;
                            const active = activeKey === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setActiveKey(t.key)}
                                    className={`text-left px-3 py-2.5 rounded-xl border transition-all ${
                                        active
                                            ? "bg-amber-50 border-amber-300 ring-2 ring-amber-100"
                                            : "bg-card border-border hover:border-amber-300"
                                    }`}
                                    data-testid={`checkin-template-${t.key}`}
                                >
                                    <div className={`flex items-center gap-1.5 mb-1 ${active ? "text-amber-700" : "text-foreground"}`}>
                                        <TIcon size={13} weight={active ? "fill" : "duotone"} />
                                        <span className="text-[12px] font-semibold">{t.label}</span>
                                    </div>
                                    <div className="text-[10.5px] text-muted-foreground leading-snug">{t.helper}</div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground pt-1">
                        Edit before sending
                    </div>
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 text-sm rounded-xl border border-border bg-background focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 resize-none"
                        placeholder="Your message…"
                        data-testid="checkin-body-textarea"
                    />
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                        <ChatCircleDots size={11} weight="duotone" />
                        Goes into the patient&apos;s in-app chat thread.
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-border bg-secondary/20 flex items-center justify-between gap-3">
                    <button
                        onClick={onClose}
                        type="button"
                        className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 h-9 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="checkin-cancel-btn"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={!canSend}
                        type="button"
                        className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        data-testid="checkin-send-btn"
                    >
                        <PaperPlaneTilt size={13} weight="bold" />
                        {sending ? "Sending…" : "Send check-in"}
                    </button>
                </div>
            </div>
        </div>
    );
}
